/*
===============================================================================================================
                                 Copyright 2022-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, {
  useState, useEffect, createContext,
} from 'react';
import { useRouter } from 'next/router';
import * as Sentry from '@sentry/nextjs';
import PropTypes from 'prop-types';
import apiClient from '@/utilities/api-client';
import LoadingScreen from '@/components/loading-screen';
import getEnvVar from '@/utilities/environment-variable';
import { useAppAuth } from '@/utilities/authorisation';
import updatePageIcons from '@/contexts/admin-context/update-page-icons';
import { NAV_VERSION } from '@/modules/page-customisation/utilities';
import { resolveApiFunction } from '@/utilities/resolve-api-path';
import { getStaticCdnPath } from '@/utilities/static-build';
import appInsights from '@/utilities/telemetry/app-insights';
import portalUtilitiesManager, { waitForPortalUtilities } from '@/utilities/lui-portal-utilities-manager';
import { useLuminatePortalConfig } from '@/contexts/luminate-portal-config-context';
import countries from '../../static-api/content/countries.json';

export const AdminContext = createContext();

const AdminContextProvider = ({ children }) => {
  const luminatePortalConfig = useLuminatePortalConfig();
  const adminOverride = getEnvVar('REACT_APP_IS_ADMIN');
  const adminOverrideValue = adminOverride ? adminOverride === 'true' || adminOverride === 'True' : null;
  const carrierOverrideValue = getEnvVar('REACT_APP_CARRIER');
  const orgIdFromEnv = getEnvVar('REACT_APP_ORGANISATION_ID');
  const orgId = luminatePortalConfig.getHostId() ?? orgIdFromEnv;
  const [config, setConfig] = useState();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [carrier, setCarrier] = useState(
    () => luminatePortalConfig.getHostId() ?? carrierOverrideValue,
  );
  const [isAdmin, setIsAdmin] = useState(adminOverrideValue);
  const [rootCompanyConfig, setRootCompanyConfig] = useState();
  const [merchantSubBrandName, setMerchantSubBrandName] = useState(null);
  const [adminModeMerchantId, setAdminModeMerchantId] = useState(null);
  const [adminNotificationEnabled, setAdminNotificationEnabled] = useState(false);
  const [forceNavV2, setForceNavV2] = useState(false);
  const [{ authData }] = useAppAuth();
  const localModeEnabled = getEnvVar('REACT_APP_LOCAL_CONFIG_MODE') ?? null;

  const configPath = localModeEnabled === 'true' ? '/api/local-admin-config' : '/api/admin-config';

  const router = useRouter();

  const getPortalInstance = () => {
    const { pathname } = router;

    const instanceCarrier = getEnvVar('REACT_APP_CARRIER');

    return {
      carrier: carrierOverrideValue || instanceCarrier,
      isAdmin: adminOverrideValue || (pathname !== '/404' && !pathname.includes('[companyId]')),
    };
  };

  const getConfig = async () => {
    async function getRemoteConfig() {
      try {
        const response = await resolveApiFunction(configPath, {
          carrier,
          token: `${authData?.token_type}  ${authData?.access_token}`,
        });
        setConfig(response.data);
        setLoading(false);
      } catch (exception) {
        setLoading(false);
        setError(exception.message);
      }
    }
    if (carrier) {
      getRemoteConfig();
    }
  };

  const fetchRootCompany = () => {
    async function getRootCompany() {
      try {
        const response = await apiClient.get(`${config.carrierName}/get-company/${config.carrierName}`);
        setRootCompanyConfig(response.data);
        setLoading(false);
      } catch (exception) {
        setLoading(false);
        setError(exception.message);
      }
    }
    if (config?.carrierName) {
      getRootCompany();
    }
  };

  /**
   * Some portals can have sub brand assets enabled on merchant level,
   * checking this here for admin-related unauthenticated pages like Login, Forgot-password, etc.
   */
  const checkForSubBrand = async (merchant) => {
    const makeRequest = async () => {
      setLoading(true);
      const { data } = await resolveApiFunction('/api/merchant-brand', {
        merchant,
        token: `${authData?.token_type}  ${authData?.access_token}`,
      });
      setMerchantSubBrandName(data?.subBrand);
    };
    try {
      await makeRequest();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getAdminCountries = (countriesObj = {}) => Object.entries(countriesObj)
    .map(([countryCode, isEnabled]) => ({
      code: countryCode,
      label: countries[countryCode],
      isEnabled,
    }))
    .sort((countryA, countryB) => countryA?.label?.localeCompare(countryB?.label));

  useEffect(() => {
    const initConfig = async () => {
      if (portalUtilitiesManager.isInIFrame()) {
        await waitForPortalUtilities();
      }

      const isInWidgetMode = portalUtilitiesManager.isWidgetModeActive();
      if (carrier && (authData || isInWidgetMode)) {
        getConfig();
      }
    };

    initConfig();
  }, [carrier, authData]);

  useEffect(() => {
    if (router.query.companyId
      && (router.query.companyId !== getEnvVar('REACT_APP_CARRIER'))
      && authData?.token_type
      && config?.features?.shouldCheckForSubBrand) {
      checkForSubBrand(router.query.companyId);
    }
  }, [config, authData?.token_type]);

  useEffect(() => {
    const hostId = luminatePortalConfig.getHostId();
    if (hostId) {
      setCarrier(hostId);
      setIsAdmin(true);
      Sentry.setTags({ carrier: hostId });
      const ai = appInsights.createAppInsights();
      appInsights.initializeTelemetry(ai, { carrier: hostId });
    }
  }, [luminatePortalConfig?.isPortal, luminatePortalConfig?.hostId]);

  useEffect(() => {
    if (luminatePortalConfig?.isPortal) return;
    if (router) {
      const portalInstance = getPortalInstance();
      if (portalInstance.carrier) {
        if (!router.asPath?.includes('token')) {
          setCarrier(portalInstance.carrier);
          setIsAdmin(portalInstance.isAdmin);

          Sentry.setTags({ carrier: portalInstance.carrier });

          const ai = appInsights.createAppInsights();
          appInsights.initializeTelemetry(ai, { carrier: portalInstance.carrier });
        }
      }
      if (!portalInstance.carrier) {
        setLoading(false);
        setError('No carrier found for this url');
      }
    }
  }, [router, luminatePortalConfig?.isPortal]);

  const getAdminConfigValue = (value) => {
    if (typeof config?.[value] !== 'undefined') {
      return config[value];
    }
    return {};
  };

  const getConfigEnvVar = (envVarPath) => {
    const envConfig = getAdminConfigValue('environmentConfig') || {};
    // if config-v2 environment variables are enabled - use them
    if (envConfig?.useConfigVariables === true) {
      if (!envConfig.variables?.[envVarPath]) {
        // eslint-disable-next-line no-console
        console.error(
          `Environment variable configuration error! You've enabled environment variables from the config-v2, but the entry is missing in the config: ${envVarPath}`,
        );
      }
      return envConfig.variables?.[envVarPath];
    }
    // otherwise use process.env variables
    return getEnvVar(envVarPath);
  };

  useEffect(() => {
    const options = getAdminConfigValue('faviconOptions');
    const productName = getAdminConfigValue('productName');
    const portalAssetUrl = `${getConfigEnvVar('REACT_APP_ASSETS_URL')}/${productName}/${orgId}`;
    const faviconOptions = {
      ...options,
      remotePath: portalAssetUrl,
    };

    const portalInstance = getPortalInstance();
    if (portalInstance.carrier && typeof productName === 'string') {
      updatePageIcons(portalInstance.carrier.toLowerCase(), faviconOptions);
    }
  }, [carrier, config]);

  if (loading) return <LoadingScreen />;

  const getAdminFeature = (featureName) => config?.features?.[featureName] ?? {};
  const getLocaleBasedFormattingEnabled = () => getAdminFeature('localeBasedFormatting')?.enabled ?? false;
  const getCarrierLogo = () => (config?.logoUrl ? `${getStaticCdnPath()}${config?.logoUrl}` : null);
  const getCarrierAltLogo = () => (config?.altLogoUrl ? `${getStaticCdnPath()}${config?.altLogoUrl}` : null);
  const getCarrierSubBrandLogoUrl = () => (config?.subBrandLogoUrl ? `${getStaticCdnPath()}${config?.subBrandLogoUrl}` : null);
  const getCarrierName = () => config?.carrierName;

  // Determines if the login page should use the external auth provider
  const getLoginWithExternal = () => {
    if (adminOverrideValue) return false; // Automatic login uses normal login page
    return !!config?.auth?.loginWithExternalAuth;
  };
  // Get the auth config object from the config
  const getAuth = () => config?.auth;

  const getEmailOnlyLogin = () => getAdminConfigValue('auth')?.emailOnly;
  const getValidationException = () => getAdminConfigValue('auth')?.validationException;

  const getAdminProductName = () => getAdminConfigValue('productName');

  const isSubBrandEnabled = () => merchantSubBrandName && merchantSubBrandName === config?.subBrandName;
  const isNavBarV2 = () => (forceNavV2 ? true : (getAdminFeature('navigation')?.version === NAV_VERSION.V2 ?? false));
  const getAdminNavRoute = () => (isNavBarV2() ? getAdminFeature('navigation')?.pages : getAdminFeature('navigation'));
  const getWidgetModeConfig = () => {
    const widgetConfig = getAdminFeature('widgetMode') ?? {};
    const appMode = luminatePortalConfig.getApplicationMode() ?? getEnvVar('NEXT_PUBLIC_APPLICATION_MODE');

    return {
      ...widgetConfig,
      isAdminMode: appMode?.toLowerCase() === 'admin',
    };
  };

  const getRbacConfig = () => {
    const rbacConfig = getAdminFeature('rbac') ?? {};
    return {
      enabled: getEnvVar('NEXT_PUBLIC_RBAC_ENABLED') === 'true',
      permissionNameSpace: rbacConfig?.permissionNameSpace,
    };
  };

  return (
    <AdminContext.Provider
      value={
        {
          carrier,
          isAdmin,
          adminModeMerchantId,
          isAdminReviewMode: Boolean(adminModeMerchantId),
          adminNotificationEnabled,
          setAdminNotificationEnabled,
          loading,
          error,
          getAdminConfigValue,
          getAdminFeature,
          getLocaleBasedFormattingEnabled,
          getCarrierName,
          getCarrierLogo,
          getCarrierAltLogo,
          getCarrierSubBrandLogoUrl,
          fetchRootCompany,
          getSupportedAdminCountries: () => getAdminCountries(rootCompanyConfig?.countriesEnabled),
          getLoginWithExternal,
          getAuth,
          getAdminProductName,
          setAdminModeMerchantId,
          setIsAdmin,
          getEmailOnlyLogin,
          getValidationException,
          rootCompanyConfig,
          isSubBrandEnabled,
          isNavBarV2,
          setForceNavV2,
          getAdminNavRoute,
          getWidgetModeConfig,
          getConfigEnvVar,
          getRbacConfig,
          ...config,
        }
      }
    >
      {children}
    </AdminContext.Provider>
  );
};

AdminContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AdminContextProvider;
